import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, useClientAuthStore, useEmployeeAuthStore, useUIStore } from '../stores';
import '../App.css';
import './LoginPage.css';

function LoginPage() {
  const [userType, setUserType] = useState('client'); // Default to client
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Auth hooks
  const { isAuthenticated: adminAuthenticated, isLoading: adminLoading } = useAuth();
  const { isAuthenticated: clientAuthenticated } = useClientAuthStore();
  const { isAuthenticated: employeeAuthenticated } = useEmployeeAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (!adminLoading) {
      if (adminAuthenticated) {
        navigate('/admin', { replace: true });
      } else if (clientAuthenticated) {
        navigate('/client/dashboard', { replace: true });
      } else if (employeeAuthenticated) {
        navigate('/employee/dashboard', { replace: true });
      }
    }
  }, [adminAuthenticated, clientAuthenticated, employeeAuthenticated, adminLoading, navigate]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Form states for different user types
  const [adminCredentials, setAdminCredentials] = useState({ username: '', password: '' });
  const [employeeCredentials, setEmployeeCredentials] = useState({ username: '', password: '' });
  const [clientCredentials, setClientCredentials] = useState({ phone: '', password: '' });

  // Get additional auth methods
  const { login: adminLogin, error: adminError } = useAuth();
  const { login: clientLogin, isLoading: clientLoading, error: clientError } = useClientAuthStore();
  const { login: employeeLogin, isLoading: employeeLoading, error: employeeError } = useEmployeeAuthStore();

  const isLoading = adminLoading || clientLoading || employeeLoading;
  const error = adminError || clientError || employeeError;

  const handleUserTypeChange = (type) => {
    setUserType(type);
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    try {
      await adminLogin({
        username: adminCredentials.username.trim(),
        password: adminCredentials.password
      });
      
      // If no error is thrown, login succeeded
      useUIStore.getState().showSuccessNotification('Login successful! Welcome to the admin panel.');
      
      // Small delay to ensure Zustand state propagates to all subscribers
      setTimeout(() => {
        navigate('/admin', { replace: true });
      }, 100);
    } catch (error) {
      useUIStore.getState().showErrorNotification(error.message || 'Login failed. Please try again.');
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    try {
      await employeeLogin({
        username: employeeCredentials.username.trim(),
        password: employeeCredentials.password
      });
      
      // If no error is thrown, login succeeded
      useUIStore.getState().showSuccessNotification('Login successful! Welcome to your employee portal.');
      
      // Small delay to ensure Zustand state propagates to all subscribers
      setTimeout(() => {
        navigate('/employee/dashboard', { replace: true });
      }, 100);
    } catch (error) {
      useUIStore.getState().showErrorNotification(error.message || 'Login failed. Please try again.');
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    try {
      await clientLogin({
        phone: clientCredentials.phone.trim(),
        password: clientCredentials.password
      });
      
      // If no error is thrown, login succeeded
      useUIStore.getState().showSuccessNotification('Login successful! Welcome to your client portal.');
      
      // Small delay to ensure Zustand state propagates to all subscribers
      setTimeout(() => {
        navigate('/client/dashboard', { replace: true });
      }, 100);
    } catch (error) {
      useUIStore.getState().showErrorNotification(error.message || 'Login failed. Please try again.');
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">Seemanchal Decor</div>
          <button 
            className="mobile-menu-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className={mobileMenuOpen ? 'hamburger open' : 'hamburger'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <li><Link to="/" onClick={closeMobileMenu} className={location.pathname === '/' ? 'active' : ''}>Home</Link></li>
            <li><a href="/#about" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#about' ? 'active' : ''}>About</a></li>
            <li><a href="/#team" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#team' ? 'active' : ''}>Team</a></li>
            <li><a href="/#services" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#services' ? 'active' : ''}>Services</a></li>
            <li><a href="/#portfolio" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#portfolio' ? 'active' : ''}>Portfolio</a></li>
            <li><a href="/#testimonials" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#testimonials' ? 'active' : ''}>Testimonials</a></li>
            <li><a href="/#contact" onClick={closeMobileMenu} className={location.pathname === '/' && location.hash === '#contact' ? 'active' : ''}>Contact</a></li>
            <li>
              <Link to="/login" onClick={closeMobileMenu} className={`nav-login-link ${location.pathname === '/login' ? 'active' : ''}`}>
                🔐 Login
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      <div className="login-page-container">
        <div className="login-page-card">
        <div className="login-page-header">
          <div className="login-icon-large">🔐</div>
          <h2>Login</h2>
          <p className="login-page-subtitle">Choose your account type to continue</p>
        </div>

        {/* User Type Selection */}
        <div className="user-type-selector">
          <button
            className={`user-type-btn ${userType === 'client' ? 'active' : ''}`}
            onClick={() => handleUserTypeChange('client')}
          >
            Client
          </button>
          <button
            className={`user-type-btn ${userType === 'employee' ? 'active' : ''}`}
            onClick={() => handleUserTypeChange('employee')}
          >
            Employee
          </button>
          <button
            className={`user-type-btn ${userType === 'admin' ? 'active' : ''}`}
            onClick={() => handleUserTypeChange('admin')}
          >
            Admin
          </button>
        </div>

        {/* Login Forms */}
        <div className="login-forms-container">
          {/* Client Login Form */}
          {userType === 'client' && (
            <form onSubmit={handleClientSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="client-phone">Phone Number</label>
                <input
                  type="tel"
                  id="client-phone"
                  value={clientCredentials.phone}
                  onChange={(e) => setClientCredentials({ ...clientCredentials, phone: e.target.value })}
                  placeholder="Enter phone number"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="client-password">Password</label>
                <input
                  type="password"
                  id="client-password"
                  value={clientCredentials.password}
                  onChange={(e) => setClientCredentials({ ...clientCredentials, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button 
                type="submit" 
                className="login-submit-button"
                disabled={isLoading || !clientCredentials.phone.trim() || !clientCredentials.password}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
              <div className="login-form-footer">
                <p>Don't have an account? <a href="/client/signup">Sign up here</a></p>
              </div>
            </form>
          )}

          {/* Employee Login Form */}
          {userType === 'employee' && (
            <form onSubmit={handleEmployeeSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="employee-username">Username</label>
                <input
                  type="text"
                  id="employee-username"
                  value={employeeCredentials.username}
                  onChange={(e) => setEmployeeCredentials({ ...employeeCredentials, username: e.target.value })}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="employee-password">Password</label>
                <input
                  type="password"
                  id="employee-password"
                  value={employeeCredentials.password}
                  onChange={(e) => setEmployeeCredentials({ ...employeeCredentials, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button 
                type="submit" 
                className="login-submit-button"
                disabled={isLoading || !employeeCredentials.username.trim() || !employeeCredentials.password}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* Admin Login Form */}
          {userType === 'admin' && (
            <form onSubmit={handleAdminSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="admin-username">Username</label>
                <input
                  type="text"
                  id="admin-username"
                  value={adminCredentials.username}
                  onChange={(e) => setAdminCredentials({ ...adminCredentials, username: e.target.value })}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-password">Password</label>
                <input
                  type="password"
                  id="admin-password"
                  value={adminCredentials.password}
                  onChange={(e) => setAdminCredentials({ ...adminCredentials, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button 
                type="submit" 
                className="login-submit-button"
                disabled={isLoading || !adminCredentials.username.trim() || !adminCredentials.password}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

export default LoginPage;

